import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import gibletLogo from '@/assets/giblet-logo.png';

export default function DemoCallForKnowledgeBounty() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBountyQuestion, setShowBountyQuestion] = useState(false);
  const [hasBounty, setHasBounty] = useState<string>('');

  const connectedToCfS = location.state?.connectedToCfS || false;

  useEffect(() => {
    // If connected to CfS, ask about bounty on that CfS
    setShowBountyQuestion(connectedToCfS);
  }, [connectedToCfS]);

  const handleNext = () => {
    navigate('/demo-creator-rewards', {
      state: { 
        ...location.state,
        cfSHasBounty: hasBounty === 'yes'
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">
            {showBountyQuestion ? 'CfS Bounty Status' : 'Ready to Review'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {showBountyQuestion 
              ? 'Does the connected Call for Solution have a bounty attached?'
              : 'Your Call for Knowledge is ready for review'}
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-6">
          {showBountyQuestion ? (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                If the Call for Solution you're supporting has a solution bounty, knowledge contributors
                may be eligible for attribution and rewards when the solution is completed.
              </p>
              
              <RadioGroup value={hasBounty} onValueChange={setHasBounty}>
                <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="yes" id="yes" />
                  <Label htmlFor="yes" className="flex-1 cursor-pointer">
                    <div className="font-semibold text-foreground">Yes, CfS has a bounty</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Knowledge contributions may share in solution rewards
                    </div>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-3 p-4 rounded-lg hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="no" id="no" />
                  <Label htmlFor="no" className="flex-1 cursor-pointer">
                    <div className="font-semibold text-foreground">No bounty on CfS</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Standard knowledge contribution attribution applies
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Your Call for Knowledge configuration is complete. In the next step, you'll see
                the Creator Rewards dashboard showing how knowledge contributions are tracked and rewarded.
              </p>
              <div className="bg-primary/10 rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-2">What happens next:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Knowledge experts can contribute their insights</li>
                  <li>• Confirmers validate the quality and accuracy</li>
                  <li>• Contributions are added to the knowledge base</li>
                  <li>• Contributors receive proper attribution and Gib tokens</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/demo-call-for-knowledge/connection')}
            className="min-w-[120px]"
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={showBountyQuestion && !hasBounty}
            className="min-w-[120px]"
          >
            {showBountyQuestion ? 'Next' : 'View Creator Rewards'}
          </Button>
        </div>
      </div>
    </div>
  );
}
